import React, { useEffect, useMemo, useRef } from 'react';
import { Link, Tooltip } from '@mui/material';
import { Title, useDataProvider } from 'react-admin';
import type { CellClickedEvent, ColDef, GridReadyEvent } from 'ag-grid-community';
import { getDefaultUtcOffset, loadSourceType } from '../../utils/common';
import { formatDate, formatDateTime, formatLocalTime } from '../../components/ex_dayjs';
import { ExchangeMarket, STATUS_ENUM, TeamOptions, getStatuses } from '../../utils/enum';
import RenderControls, { ControlConfig } from '../../components/ra-list/RenderControls';
import AgGrid from '../../components/ag-grid/ag-grid';
import AssignmentUser from './components/AssignmentUser';
import Assign from './components/Assign';
import { FilterListProvider } from '../../context/FilterListProvider';
import ChartButton from './components/chart-button';
import { IAssignmentUser, IChoice } from './types';

const _resource = 'OrderAssignments';
const allowMarketId = [1, 2, 13];
const allowStatuses = [STATUS_ENUM.Unprocessed, STATUS_ENUM.PendingShipment];
const allowVividStatus = getStatuses([...allowStatuses, STATUS_ENUM.Processing, STATUS_ENUM.Completed]);
const allowTNStatus = getStatuses([...allowStatuses, STATUS_ENUM.TNProcessing, STATUS_ENUM.Completed]);

/**
 * Order Assignments list page: an `AgGrid` of orders with an editable
 * `assignedTo` column (`AssignmentUser`/`Assign` cell editor/renderer), and
 * the filter/chart toolbar above it.
 */
const OrderAssignments = () => {
    const dataProvider = useDataProvider();
    const refInput = useRef<any>(null);
    const refbtnFilter = useRef<HTMLButtonElement>(null);
    const [marketType, setMarketType] = React.useState<IChoice[]>([]);
    const [listUser, setListUser] = React.useState<IAssignmentUser[]>([]);
    const [orderStatuses, setOrderStatuses] = React.useState<IChoice[]>(allowVividStatus);
    const [defaultValue] = React.useState<Record<string, any>>({
        IsAssigned: null,
        AssignedDate_eq: new Date(),
        OrderStatus: STATUS_ENUM.Unprocessed,
        utc_offset_eq: getDefaultUtcOffset()
    });

    const [defaultValuesAG] = React.useState<{ sortField: { field: string; order: string } }>({
        sortField: { field: 'inhandDate', order: 'asc' },
    });

    function handleSelection (e: { name: string; value: unknown }) {
        const value = e.value;
        // eslint-disable-next-line eqeqeq -- loose check intentionally matches both string "2" and number 2
        if (value == 2) setOrderStatuses(allowTNStatus);
        else setOrderStatuses(allowVividStatus);
    }
    const controls: ControlConfig[] = [
        // eslint-disable-next-line eqeqeq -- loose check intentionally matches both string and number ids
        { component: 'select', name: 'Market', label: 'Exchange', choices: ExchangeMarket.filter((x: any) => allowMarketId.find((y: any) => y == x.id)), onChange: (e) => handleSelection(e), xs: 1 },
        { component: 'select', name: 'MarketType_eq', label: 'Type', choices: marketType },
        { component: 'number', name: 'OrderNumber', label: 'Order #', xs: 1 },
        { component: 'select', name: 'OrderStatus', label: 'Order Status', choices: orderStatuses, xs: 1.5 },
        { component: 'select', name: 'TeamId', choices: TeamOptions, label: 'Team' },
        { component: 'autocomplete', name: 'AssignedTo_eq', label: 'Assigned to', choices: listUser.map((x) => x.name), isMulti: false },
        { component: 'date', name: 'AssignedDate_eq', label: 'Assigned Date', isUTC: true, xs: 1.2 },
        { component: 'checkbox', name: 'IsAssigned', label: 'Assigned' },
        { component: 'checkbox', name: 'IsInhandDate', label: 'IHD', xs: 0.5 },
        { component: 'text', name: 'utc_offset', isHide: true }
    ];

    const ragRenderer = (params: any) => {
        switch (params.column?.colId) {
            case 'market': {
                // eslint-disable-next-line eqeqeq -- loose check intentionally matches both string and number ids
                let marketName = ExchangeMarket.find((x: any) => x.id == params.value)?.name;
                return (<span>{marketName}</span>);
            }
            case 'orderDate':
            case 'eventDate':
                return <span>{formatDateTime(params.value)}</span>;
            case 'inhandDate': {
                let color = '';
                let daysToInhand = params.data.daysToInhand;
                // eslint-disable-next-line eqeqeq -- intentionally matches both null and undefined
                if (daysToInhand != null) {
                    if (daysToInhand < 6) color = 'yellow';
                    if (daysToInhand < 3) color = 'red';
                }
                return (
                    // eslint-disable-next-line eqeqeq -- intentionally matches both null and undefined
                    <Tooltip arrow title={daysToInhand != null ? `Processed at ${formatLocalTime(params.data.utcPostedOn)}` : null}>
                        <span style={{ color: color }}>{formatDate(params.value)}</span>
                    </Tooltip>
                );
            }
            case 'assignedDate': // for utc
                return <span>{params.value ? formatLocalTime(params.value) : null}</span>;
            case 'eventName':
                return (
                    <Link href={params.data.eventUrl} underline="hover" target="_blank">
                        <span>{params.value}</span>
                    </Link>
                );
            case 'orderStatus': {
                // eslint-disable-next-line eqeqeq -- loose check intentionally matches both string and number ids
                let vividStatus = allowVividStatus.find((x: any) => x.id == params.value)?.name;
                // eslint-disable-next-line eqeqeq -- loose check intentionally matches both string and number ids
                let tnStatus = allowTNStatus.find((x: any) => x.id == params.value)?.name;
                return (
                    <span>{vividStatus || tnStatus}</span>
                );
            }
            default:
                return <span>{params.value}</span>;
        }
    };

    let colDefs: ColDef[] = [
        { field: 'market', headerName: 'Market', cellRenderer: ragRenderer, maxWidth: 120 },
        { field: 'orderNumber', headerName: 'Order #', maxWidth: 100 },
        { field: 'orderStatus', headerName: 'Order Status', cellRenderer: ragRenderer, maxWidth: 150 },
        { field: 'orderDate', headerName: 'Order Date', cellRenderer: ragRenderer, maxWidth: 150 },
        { field: 'marketType', headerName: 'Type', maxWidth: 100 },
        { field: 'eventName', headerName: 'Event', cellRenderer: ragRenderer, maxWidth: 300 },
        { field: 'venue', headerName: 'Venue', cellRenderer: ragRenderer, maxWidth: 300 },
        { field: 'eventDate', headerName: 'Event Date', cellRenderer: ragRenderer, maxWidth: 150 },
        { field: 'inhandDate', headerName: 'IHD', cellRenderer: ragRenderer, maxWidth: 100 },
        { field: 'assignedDate', headerName: 'Assigned Date', cellRenderer: ragRenderer, maxWidth: 150 },
        {
            field: 'assignedTo', headerName: 'Processor', editable: true,
            cellEditor: (props: any) => <AssignmentUser {...props} listUser={listUser} />,
            cellRenderer: (props: any) => <Assign {...props} listUser={listUser} />,
            cellEditorPopup: true,
            colId: 'assignedTo',
            cellEditorPopupPosition: 'over'
        }
    ];

    const defaultColDef = useMemo(() => {
        return {
            cellStyle: { wordBreak: 'normal', fontSize: '14px' },
            singleClickEdit: true,
            sortable: true,
            wrapText: true,
            filter: false,
            autoHeight: true,
            flex: 1,
            suppressRowHoverHighlight: true,
            suppressHorizontalScroll: true,
            suppressMovable: true,//block move columns
            resizable: true,
            autoHeaderHeight: true,
        };
    }, []);

    useEffect(() => {
        loadSourceType(dataProvider, (response: IChoice[]) => {
            setMarketType(response);
        });
        dataProvider
            .fetchData(_resource, 'GetTeam')
            .then((response: any) => {
                if (response.data) {
                    let newData = response.data.map((x: any) => ({ id: x.id, name: x.email, value: x.aliasName }));
                    setListUser(newData);
                }
            })
            .catch((error: any) => console.log(error));
    }, []);

    const handleSearch = (filterList: Record<string, unknown>) => {
        refInput.current.customFilterSearch(filterList);
    };

    const onGridReady = ((params: GridReadyEvent) => {
        let defaultSortModel = [
            { colId: 'inhandDate', sort: 'asc', sortIndex: 0 }
        ];
        (params as any).columnApi.applyColumnState({ state: defaultSortModel });
    });

    return (
        <>
            <Title title={'Order Assignments'}></Title>
            <FilterListProvider>
                <RenderControls
                    controls={controls}
                    onSearch={handleSearch}
                    defaultValues={defaultValue}
                    refFilter={refbtnFilter}
                    agSource={_resource}
                    agSort={defaultValuesAG.sortField}
                    actions={<ChartButton />}
                    exportFile={{
                        filename: `${_resource}-${formatDateTime(new Date(), 'YY-MM-DD')}`,
                        columns: ['Market', 'OrderNumber', 'OrderStatus', 'OrderDate', 'MarketType', 'EventName', 'Venue', 'EventDate', 'InhandDate', 'AssignedDate', 'AssignedTo']
                    }}
                />
            </FilterListProvider>
            <AgGrid
                ref={refInput}
                list={_resource}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                defaultValues={defaultValuesAG}
                rowHeight={45}
                rowSelection='single'
                onGridReadyCallback={onGridReady}
                onCellClicked={(params: CellClickedEvent) => {
                    // Prevent click cell to edit when click delete icon in AssignmentUser component
                    if ((params.event?.target as HTMLElement)?.closest('.MuiChip-deleteIcon')) {
                        return;
                    }
                }}
            ></AgGrid>
        </>
    );
};

const OrderAssignmentsPage = {
    name: _resource,
    list: OrderAssignments
};
export default OrderAssignmentsPage;